import { useState, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material'
import {
  Download as DownloadIcon,
  TableChart as TableIcon,
  CalendarMonth as TimetableIcon,
  Person as TeacherIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { getAcademicYearsDirect, generateExportDataDirect, getTeachersDirect, getClassSectionsDirect } from '@timetable/api'
import { generateMasterDataPdf, generateTimetablePdf, generateTeacherTimetablePdf, generateFullExportWorkbook } from '@timetable/utils'
import { useAuthStore } from '@/store/authStore'

type ExportType = 'master' | 'timetable' | 'teacher'

export function ExcelExport() {
  const { schoolContext, user } = useAuthStore()
  const schoolName = (user as any)?.school_name ?? schoolContext ?? ''

  const [exportType, setExportType] = useState<ExportType>('master')
  const [selectedYear, setSelectedYear] = useState<string>('')
  const [allClasses, setAllClasses] = useState(false)
  const [selectedClasses, setSelectedClasses] = useState<string[]>([])
  const [allTeachers, setAllTeachers] = useState(false)
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([])
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const { data: academicYears = [], isLoading: yearsLoading } = useQuery({
    queryKey: ['academic-years-all', schoolContext],
    queryFn: () => getAcademicYearsDirect({ school_id: schoolContext! }),
    enabled: !!schoolContext,
  })

  const { data: exportData, isLoading: dataLoading, error: dataError } = useQuery({
    queryKey: ['export-data', schoolContext, selectedYear],
    queryFn: () => generateExportDataDirect(schoolContext!, selectedYear),
    enabled: !!schoolContext && !!selectedYear,
    staleTime: 0,
    gcTime: 0,
  })

  // Fetch teachers and class sections directly — independent of timetable data
  const { data: allTeachersList = [] } = useQuery({
    queryKey: ['teachers-for-export', schoolContext],
    queryFn: () => getTeachersDirect({ school_id: schoolContext! }),
    enabled: !!schoolContext,
  })

  const { data: allSectionsList = [] } = useQuery({
    queryKey: ['sections-for-export', schoolContext],
    queryFn: () => getClassSectionsDirect(schoolContext!),
    enabled: !!schoolContext,
  })

  const availableClasses = useMemo(() => {
    // Use timetable rows if available, otherwise fall back to class sections
    if (exportData?.timetableRows.length) {
      return [...new Set(exportData.timetableRows.map((r) => `${r.class}/${r.section}`))].sort()
    }
    if (allSectionsList.length) {
      return allSectionsList
        .map((s: any) => `${s.class?.class_name ?? ''}/${s.section}`)
        .filter((s: string) => s !== '/')
        .sort()
    }
    return exportData?.classes.map((c) => c.class_name).sort() ?? []
  }, [exportData, allSectionsList])

  const availableTeachers = useMemo(() => {
    // Use timetable rows if available, otherwise fall back to teachers list
    if (exportData?.timetableRows.length) {
      return [...new Set(exportData.timetableRows.map((r) => r.teacher))].sort()
    }
    return allTeachersList.map((t: any) => t.name).sort()
  }, [exportData, allTeachersList])

  const canExport =
    !!selectedYear &&
    !exporting &&
    (exportType === 'master' ||
      (exportType === 'timetable' && (allClasses || selectedClasses.length > 0)) ||
      (exportType === 'teacher' && (allTeachers || selectedTeachers.length > 0)))

  const handleTypeChange = (_: any, v: ExportType | null) => {
    if (!v) return
    setExportType(v)
    setError(null)
    setSuccess(false)
  }

  const handleYearChange = (year: string) => {
    setSelectedYear(year)
    setSelectedClasses([])
    setSelectedTeachers([])
    setError(null)
    setSuccess(false)
  }

  const handleExport = async () => {
    if (!canExport || !schoolContext) return
    setExporting(true)
    setError(null)
    setSuccess(false)

    try {
      const data = await generateExportDataDirect(schoolContext, selectedYear)
      let blob: Blob
      let filename: string

      if (exportType === 'master') {
        blob = generateMasterDataPdf(data, schoolName)
        filename = `master-data-${schoolContext}-${selectedYear}.pdf`
      } else if (exportType === 'timetable') {
        blob = generateTimetablePdf(data, allClasses ? [] : selectedClasses, schoolName)
        filename = `timetable-${schoolContext}-${selectedYear}.pdf`
      } else {
        blob = generateTeacherTimetablePdf(data, allTeachers ? [] : selectedTeachers, schoolName)
        filename = `teacher-timetable-${schoolContext}-${selectedYear}.pdf`
      }

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const handleExcelExport = async () => {
    if (!selectedYear || !schoolContext) return
    setExporting(true)
    setError(null)
    setSuccess(false)
    try {
      const data = await generateExportDataDirect(schoolContext, selectedYear)
      const blob = generateFullExportWorkbook(data)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `master-data-${schoolContext}-${selectedYear}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Excel export failed')
    } finally {
      setExporting(false)
    }
  }

  const MultiSelect = ({
    label,
    id,
    options,
    value,
    onChange,
  }: {
    label: string
    id: string
    options: string[]
    value: string[]
    onChange: (v: string[]) => void
  }) => (
    <FormControl fullWidth sx={{ mt: 1 }}>
      <InputLabel id={id}>{label}</InputLabel>
      <Select
        labelId={id}
        multiple
        value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? [e.target.value] : e.target.value as string[])}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((v) => <Chip key={v} label={v} size="small" />)}
          </Box>
        )}
        disabled={dataLoading}
      >
        {dataLoading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            <Checkbox checked={value.includes(opt)} />
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <Box sx={{ px: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Export School Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate a PDF of master data, class timetables, or teacher timetables.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Export Type</Typography>
        <ToggleButtonGroup
          value={exportType}
          exclusive
          onChange={handleTypeChange}
          sx={{ mb: 3, flexWrap: 'wrap' }}
          size="small"
        >
          <ToggleButton value="master">
            <TableIcon sx={{ mr: 1 }} fontSize="small" />
            Master Data
          </ToggleButton>
          <ToggleButton value="timetable">
            <TimetableIcon sx={{ mr: 1 }} fontSize="small" />
            Class Timetable
          </ToggleButton>
          <ToggleButton value="teacher">
            <TeacherIcon sx={{ mr: 1 }} fontSize="small" />
            Teacher Timetable
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="export-year-label">Academic Year</InputLabel>
          <Select
            labelId="export-year-label"
            label="Academic Year"
            value={selectedYear}
            onChange={(e) => handleYearChange(e.target.value)}
            disabled={yearsLoading}
          >
            {academicYears.map((ay) => (
              <MenuItem key={ay.id} value={ay.name}>{ay.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {exportType === 'timetable' && selectedYear && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={allClasses} onChange={(e) => { setAllClasses(e.target.checked); if (e.target.checked) setSelectedClasses([]) }} />}
              label="All Classes"
            />
            {!allClasses && (
              <MultiSelect
                label="Select Classes"
                id="class-select-label"
                options={availableClasses}
                value={selectedClasses}
                onChange={setSelectedClasses}
              />
            )}
          </Box>
        )}

        {exportType === 'teacher' && selectedYear && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={allTeachers} onChange={(e) => { setAllTeachers(e.target.checked); if (e.target.checked) setSelectedTeachers([]) }} />}
              label="All Teachers"
            />
            {!allTeachers && (
              <MultiSelect
                label="Select Teachers"
                id="teacher-select-label"
                options={availableTeachers}
                value={selectedTeachers}
                onChange={setSelectedTeachers}
              />
            )}
          </Box>
        )}

        {dataError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Failed to load timetable data: {dataError instanceof Error ? dataError.message : 'Unknown error'}
          </Alert>
        )}
        {exportData && exportData.timetableRows.length === 0 && selectedYear && !dataLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No timetable records found for {selectedYear}. Teachers and classes will not be available for selection.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={!canExport}
          fullWidth
        >
          {exporting ? 'Generating PDF…' : 'Export PDF'}
        </Button>
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
          onClick={handleExcelExport}
          disabled={!selectedYear || exporting}
          fullWidth
          sx={{ mt: 1 }}
        >
          {exporting ? 'Generating…' : 'Export as Excel (re-importable)'}
        </Button>
      </Paper>

      {success && (
        <Alert severity="success">
          <AlertTitle>Export Successful</AlertTitle>
          <Typography variant="body2">Your PDF has been downloaded.</Typography>
        </Alert>
      )}
    </Box>
  )
}
